<script>
import { mapActions } from 'vuex';
import SwitchButton from '../../../components/SwitchButton.vue';
import i18n from "@/plugins/i18n";

export default {
    components: {
        SwitchButton
    },

    data() {
        return {
            loading: false,
        
            electricityTerms: [],
            calculationTerms: [],
        }
    },

    computed: {
        tableHeaders() {
            const start = "adminPanel.tables.carbon.";
            return [
                {text: i18n.t(start + "year")},
                {text: i18n.t(start + "yearly")},
                {text: i18n.t(start + "quarterly")}
            ];
        },

        electricVehicleHeaders() {
            const start = "adminPanel.tables.carbon.";
            return [
                {text: i18n.t(start + "year")},
                {text: i18n.t(start + "included")},
                {text: i18n.t(start + "not_included")}
            ];
        }
    },

    async mounted() {
        await this._getTermsAndStatesInputs();
    },

    methods: {
        ...mapActions("admin", [
            "getTermsAndStatesInputs",
            "updateTermsAndStates"
        ]),

        async _getTermsAndStatesInputs() {
            await this.setLoading(true);

            this.getTermsAndStatesInputs()
                .then( async (res) => {
                    this.calculationTerms = res.data.filter(item => item.carbon_term_state_id == 3);
                    this.electricityTerms = res.data.filter(item => item.carbon_term_state_id == 4);
                })
                .catch(({ response }) => {
                    this.$notify("error", response.messageName);
                })
                .finally(async () => {
                    await this.setLoading(false);
                })
        },

        async updateElectricity(item, bool) {
            const params = {
                carbon_terms_states_input_id: item.carbon_terms_states_input_id,
                is_electric_included: bool
            };

            this.updateTermsAndStates(params)
                .then( async (res) => {
                    await this.$notify("success", res.data.messageName);
                    await this._getTermsAndStatesInputs();
                })
                .catch(({ response }) => {
                    this.$notify("error", response.messageName);
                })
        },

        async updateCalculation(item, bool) {
            const params = {
                carbon_terms_states_input_id: item.carbon_terms_states_input_id,
                is_yearly: bool
            };

            this.updateTermsAndStates(params)
                .then( async (res) => {
                    await this.$notify("success", res.data.messageName);
                    await this._getTermsAndStatesInputs();
                })
                .catch(({ response }) => {
                    this.$notify("error", response.messageName);
                })
        },

        async setLoading(bool) {
            this.loading = bool;
        },
    }
}
</script>

<template>
    <div class="carbon-profile-page">
        <div class="electricity-term" v-if="electricityTerm != null">
            <div class="text">
                <i class="fa-solid fa-circle-question"
                    v-sust-tooltip="{ 
                        text: $t('adminPanel.explanations.carbon.electric2'),
                        fontSize: '1em'
                    }"
                ></i>
                <span v-if="electricityTerm.is_electric_included" v-html="$t('adminPanel.explanations.carbon.electric1+')"></span>
                <span v-else v-html="$t('adminPanel.explanations.carbon.electric1-')"></span>
            </div>
            <SwitchButton
                @buttonState="updateElectricity"
                :initialValue="electricityTerm.is_electric_included"
            />
        </div>

        <div class="calculation-terms">
            <div class="headers">
                <div class="header" v-for="(header, index) in electricVehicleHeaders" :key="index" :class="{'center-info-icon': index == 0}">
                    {{ header.text }}
                    <div class="electricity-term" v-if="index == 0">
                        <div class="text">
                            <i class="fa-solid fa-circle-question"
                                v-sust-tooltip="{ 
                                    text: $t('adminPanel.explanations.carbon.electric2'),
                                    fontSize: '1em'
                                }"
                            ></i>
                        </div>
                    </div>
                </div>
            </div>

            <div class="terms" v-for="item in electricityTerms" :key="item.carbon_terms_states_input_id">
                <div class="term">
                    <div class="text">
                        {{ item.carbon_year }}
                    </div>
                    <div class="selection" :class="{'selected': item.is_electric_included == true}">
                        <i @click="updateElectricity(item, true)" class="fa-solid fa-circle-check" v-if="item.is_electric_included == true"></i>
                        <i @click="updateElectricity(item, true)" class="fa-regular fa-circle" style="color: gray !important;" v-else></i>
                    </div>
                    <div class="selection" :class="{'selected': item.is_electric_included == false}">
                        <i @click="updateElectricity(item, false)" class="fa-solid fa-circle-check" v-if="item.is_electric_included == false"></i>
                        <i @click="updateElectricity(item, false)" class="fa-regular fa-circle" style="color: gray !important;" v-else></i>
                    </div>
                </div>
            </div>
        </div>

        <div class="calculation-terms">
            <div class="headers">
                <div class="header" v-for="header in tableHeaders" :key="header.text">
                    {{ header.text }}
                </div>
            </div>

            <div class="terms" v-for="item in calculationTerms" :key="item.carbon_terms_states_input_id">
                <div class="term">
                    <div class="text">
                        {{ item.carbon_year }}
                    </div>
                    <div class="selection" :class="{'selected': item.is_yearly == true}">
                        <i @click="updateCalculation(item, true)" class="fa-solid fa-circle-check" v-if="item.is_yearly == true"></i>
                        <i @click="updateCalculation(item, true)" class="fa-regular fa-circle" style="color: gray !important;" v-else></i>
                    </div>
                    <div class="selection" :class="{'selected': item.is_yearly == false}">
                        <i @click="updateCalculation(item, false)" class="fa-solid fa-circle-check" v-if="item.is_yearly == false"></i>
                        <i @click="updateCalculation(item, false)" class="fa-regular fa-circle" style="color: gray !important;" v-else></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .carbon-profile-page {
        box-shadow: 0 .1em .5em .25em gray;
        padding: 1em;
        background-color: whitesmoke;
        border-radius: 1em;

        padding-top: 1em;

        > .calculation-terms {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            width: 100%;

            &:first-of-type {
                padding-bottom: 2em;
            }
            &:last-of-type {
                border-top: .3em solid lightgray;
                padding-top: 1em;
                > .terms {
                    &:last-of-type {
                        > .term {
                            border: 0 !important;
                        }
                    }
                }
            }

            > .headers {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                width: 550px;
                font-size: 1.2em;
                font-weight: bold;
                color: black;

                border-bottom: thin solid gray;

                > .header {
                    text-align: center;
                    padding-left: 0 !important;
                    padding: .33em 1em .33em 0;

                    width: 250px !important;

                    &:first-of-type {
                        text-align: left;
                    }
                }

                > .center-info-icon {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                }
            }

            > .terms {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                width: 550px;

                > .term {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                    width: 100%;
                    font-size: 1.1em;
                    font-weight: bold;
                    color: black;

                    border-bottom: thin solid gray;

                    > div {
                        padding: .33em 1em .33em 0;
                        width: 250px !important;

                        &:first-of-type {
                            padding-left: .05em;
                        }
                    }

                    > .selection {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;

                        > i {
                            color: var(--sustainfinity-green);
                            
                            cursor: pointer;
                            transition: all 150ms;
                            &:hover {
                                filter: brightness(50%);
                            }
                        }
                    }

                    > .selected > i {
                        pointer-events: none;
                    }
                }
            }
        }
    }
    
    .electricity-term {
        margin-left: 7px; 
        max-width: 3px;
        z-index: 99;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%; 
        overflow: visible;
        > .text {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            padding-right: 1em;
            font-weight: bold;
            font-size: 1.2em;
            color: black;
            > i {
                padding-right: .5em;
            }
        }
        position: relative;
        > .my-info-tooltip {
            transition: all 150ms;
            position: absolute;
            top: 1.2em;
            left: 1em;
            width: 300px;
            height: fit-content;
            border-radius: 7px;
            border: thin solid lightgray;
            background-color: rgba(2, 48, 71, 0.85);
            color: whitesmoke;
            padding: .66em;
            animation: fadeIn 250ms ease-in;
        }
    }

    @keyframes fadeIn {
        0% {
            opacity: 0;
        } 100% {
            opacity: 100%;
        }
    }
</style>